import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { PlanStateService } from "@app/shared/services/plan-state.service";
import { Course } from "@core/domain/course";
import {
  ExamComponent,
  ExamComponentMandatoryChoiceType,
  Minor,
  MinorExamComponent,
} from "@core/domain/exam-component";
import { CourseHelper } from "@core/domain/helpers/course-helper";
import { PlanHelper } from "@core/domain/helpers/plan-helper";
import { PlanDetails } from "@core/domain/plan-details";
import { Specialisation } from "@core/domain/plan-later";
import { TranslateService } from "@ngx-translate/core";
import { CourseDataMapper } from "@shared/services/course-data-mapping.service";
import { PlanLaterStateService } from "@shared/services/plan-later-state.service";
import { CourseDataAction, CourseDataGroup, CourseList } from "@shared/types/course-data";
import { PlanLaterType } from "@shared/types/plan-later-types";

export class PlanLaterCourseGroupBuilder {
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly courseHelper = inject(CourseHelper);
  private readonly planHelper = inject(PlanHelper);
  private readonly courseDataMapper = inject(CourseDataMapper);
  private readonly planStateService = inject(PlanStateService);

  buildExamComponentCourseGroup(
    examComponent: ExamComponent,
    type: PlanLaterType,
    selectedPlan: PlanDetails | undefined,
  ): CourseDataGroup {
    const labelSuffix = examComponent.label ? ` - ${examComponent.label}` : "";
    const title = `${examComponent.examComponentName}${labelSuffix}`;
    const subTitle = examComponent.examComponentExplanation;

    let header = "";
    if (type === PlanLaterType.MANDATORY) {
      header = this.translate.instant("examComponent.mandatory") as string;
    }
    if (type === PlanLaterType.MANDATORY_CHOICE && !examComponent.conditionsMet) {
      header = this.translate.instant("examComponent.mandatoryChoice", {
        minimumPoints: examComponent.minimumPoints,
      }) as string;
    }
    const courseList: CourseList = this.buildCourseList(header, examComponent.courses, type, selectedPlan);

    return {
      id: examComponent.id ? examComponent.id.toString() : title,
      code: examComponent.examComponentCode,
      title,
      subTitle,
      coursesLinkExamComponentName: this.getCoursesLinkExamComponentName(examComponent),
      courseLists: [courseList],
    } as CourseDataGroup;
  }

  buildSpecialisationCourseGroup(
    specialisation: Specialisation,
    examComponents: ExamComponent[],
    selectedPlan: PlanDetails | undefined,
    planLaterStateService: PlanLaterStateService,
  ): CourseDataGroup {
    const title = `${specialisation.code} - ${specialisation.name}`;
    const subTitle = specialisation.isEnrolled ? "planLater.enrolled" : "planLater.planned";
    const subTitleClass = specialisation.isEnrolled ? "status-enrolled" : "status-planned";

    const specialisationCourseLists = examComponents.map((examComponent) => {
      const examCompTypeLabel = this.planHelper.getExamComponentTypeLabel(
        examComponent.type,
        examComponent.minimumPoints,
      );
      const labelSuffix = examCompTypeLabel ? ` - ${examCompTypeLabel}` : "";
      const header = `${examComponent.examComponentName}${labelSuffix}`;
      return this.buildCourseList(header, examComponent.courses, PlanLaterType.SPECIALISATION, selectedPlan);
    });

    const specialisationCourseDataGroup = {
      id: this.planHelper.stripSpaces(specialisation.name),
      code: specialisation.code,
      title,
      subTitle: this.translate.instant(subTitle) as string,
      subTitleClass,
      courseLists: specialisationCourseLists,
    } as CourseDataGroup;

    if (this.planStateService.isRemoveSpecialisationFromPlanLaterAllowed(specialisation)) {
      specialisationCourseDataGroup.actions = [
        {
          title: this.translate.instant("label.remove") as string,
          icon: "trash",
          onAction: (): void => {
            // When the remove icon is clicked for the specialisation in Plan Later:
            setTimeout(() => {
              planLaterStateService.removeSpecialisation(specialisation);
            }, 0);
          },
        } as CourseDataAction,
      ];
    }

    return specialisationCourseDataGroup;
  }

  buildMinorCourseGroup(
    minor: Minor,
    minorExamComponents: MinorExamComponent[],
    selectedPlan: PlanDetails | undefined,
    planLaterStateService: PlanLaterStateService,
  ): CourseDataGroup {
    // old code shows that sometimes minor.inhoud is shown as "more details" (with or without ellipsis).
    // See MinorComponent.html (getInhoud()) - not done in this implementation.
    const title = minor.name;
    const subTitle = minor.isEnrolled ? (this.translate.instant("planLater.enrolled") as string) : undefined;
    const subTitleClass = minor.isEnrolled ? "status-enrolled" : undefined;

    // For each examComponent build a courseList
    const planLaterCourseLists = minorExamComponents.map((examComponent) => {
      const header = this.constructMinorExamComponentHeader(examComponent);
      return this.buildCourseList(header, examComponent.courses, PlanLaterType.ELECTIVES_MINOR, selectedPlan);
    });

    const minorCourseDataGroup: CourseDataGroup = {
      id: this.planHelper.stripSpaces(minor.name),
      code: minor.minor,
      title,
      subTitle,
      subTitleClass,
      courseLists: planLaterCourseLists,
      actions: [
        {
          title: this.translate.instant("courseControl.showMinorInfo") as string,
          icon: "information-circle",
          onAction: () => {
            this.onMinorShowInfo(minor);
          },
        } as CourseDataAction,
      ],
    } as CourseDataGroup;
    // add delete action only if the minor is not enrolled and not part of a locked proposal
    if (this.planStateService.isRemoveMinorFromPlanLaterAllowed(minor, minorExamComponents)) {
      minorCourseDataGroup.actions?.push({
        title: this.translate.instant("label.remove") as string,
        icon: "trash",
        onAction: () => {
          setTimeout(() => {
            // NOTE: without this timeout the menu will not close. Not sure why.
            planLaterStateService.removeMinor(minor);
          }, 0);
        },
      } as CourseDataAction);
    }
    return minorCourseDataGroup;
  }

  buildCoursesGroup(
    title: string,
    subTitle: string | undefined,
    header: string | undefined,
    courses: Course[],
    type: PlanLaterType,
    selectedPlan: PlanDetails | undefined,
  ): CourseDataGroup {
    return {
      id: title,
      code: title,
      title: title ? (this.translate.instant(title) as string) : "",
      subTitle,
      courseLists: [this.buildCourseList(header, courses, type, selectedPlan)],
    } as CourseDataGroup;
  }

  private buildCourseList(
    header: string | undefined,
    courses: Course[],
    type: PlanLaterType,
    selectedPlan: PlanDetails | undefined,
  ): CourseList {
    const isRemovable =
      type === PlanLaterType.ELECTIVES_COURSES ||
      type === PlanLaterType.OTHER_COURSES ||
      type === PlanLaterType.REPLACEMENT_COURSES;
    const courseDataList = courses
      .map((course) => this.courseHelper.findPlannedCourse(course, selectedPlan) ?? course)
      .map((course) =>
        this.courseDataMapper.mapCourseData(course, {
          selectedPlan: selectedPlan,
          isPlannable: true,
          isRemovable: isRemovable,
          planLaterType: type,
        }),
      );

    return {
      header,
      courseDataList: courseDataList,
    };
  }

  private constructMinorExamComponentHeader(examComponent: MinorExamComponent): string {
    let header = "";

    if (examComponent.type) {
      const examCompTypeLabel = this.planHelper.getExamComponentTypeLabel(
        examComponent.type,
        examComponent.minimumPoints,
      );
      if (examComponent.minorExamComponentName) {
        header = `${examComponent.minorExamComponentName} - ${examCompTypeLabel}`;
      }

      if (examComponent.label) {
        header = `${header} - ${examComponent.label}`;
      }
    }

    return header;
  }

  private onMinorShowInfo(minor: Minor): void {
    const minorStudyProgram = this.planHelper.stripSpaces(minor.studyProgram);
    const minorMinor = this.planHelper.stripSpaces(minor.minor);
    this.router
      .navigate(["/catalog/minors"], {
        queryParams: {
          minor: minorMinor,
          minorCode: minorMinor,
          minorName: minor.name,
          minorDescription: `${minorStudyProgram} ${minor.name}`,
        },
      })
      .catch((error) => {
        console.error(`Error when showing info for minor ${minorStudyProgram} ${minor.name}:`, error);
      });
  }

  private getCoursesLinkExamComponentName(examComponent: ExamComponent): string | undefined {
    return examComponent.typeMandatoryChoice === ExamComponentMandatoryChoiceType.CONDITION
      ? examComponent.examComponentName
      : undefined;
  }
}
